
import React, { useState, useEffect } from 'react';
import { AppSettings, TicketPriority, DashboardCardVisibilityConfig, Employee, SLARule } from '../types';
import { Button } from './Button';
import { DEFAULT_APP_SETTINGS, SQUARE_SANDBOX_APP_ID, SQUARE_SANDBOX_LOCATION_ID, SLA_RULES } from '../constants';

interface AdminSettingsPageProps {
  settings: AppSettings;
  onSaveSettings: (updatedSettings: Partial<AppSettings>) => void;
  currentUser: Employee | null;
}

const CogWheelIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 mr-2 text-[#003794]" {...props}>
    <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.925 1.542A12.026 12.026 0 006.34 6.339c-1.356 1.356-2.027 3.11-.974 4.625a3.75 3.75 0 016.113 .63H12a.75.75 0 010 1.5h2.538a3.75 3.75 0 01-6.113.63c-1.053-1.515-.382-3.27.974-4.625a12.026 12.026 0 002.514-2.543c.226-.88.908-1.542 1.925-1.542zM9.22 21.75a3.75 3.75 0 01-6.113-.63c-1.053-1.515-.382-3.27.974-4.625a12.026 12.026 0 002.514-2.543c.226-.88.908-1.542 1.925-1.542.917 0 1.699.663 1.925 1.542a12.026 12.026 0 002.514 2.543c1.356 1.356 2.027 3.11.974 4.625a3.75 3.75 0 01-6.113.63zM4.509 9.332l-.003-.002a3.745 3.745 0 01.714-5.442A12.006 12.006 0 009.75 2.25h4.5c1.512 0 2.924.556 3.997 1.534a3.745 3.745 0 01.714 5.442l-.003.002a12.006 12.006 0 001.645 2.425a3.745 3.745 0 010 5.486 12.006 12.006 0 00-1.645 2.425l.003.002a3.745 3.745 0 01-.714 5.442A12.006 12.006 0 0014.25 21.75h-4.5a12.006 12.006 0 00-4.532-1.645l-.003-.002a3.745 3.745 0 01-.714-5.442 12.006 12.006 0 00-1.645-2.425a3.745 3.745 0 010-5.486 12.006 12.006 0 001.645-2.425z" clipRule="evenodd" />
    <path d="M12 12.75a.75.75 0 100-1.5.75.75 0 000 1.5z" />
  </svg>
);

const ShieldCheckIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-slate-600" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
  </svg>
);


export const AdminSettingsPage: React.FC<AdminSettingsPageProps> = ({ settings, onSaveSettings, currentUser }) => {
  const [formData, setFormData] = useState<AppSettings>(settings);

  useEffect(() => {
    // Ensure dashboardCardVisibility is initialized if not present in settings (it's managed elsewhere now)
    const initialSettings = {
      ...DEFAULT_APP_SETTINGS,
      ...settings,
      // dashboardCardVisibility is no longer managed here, but ensure AppSettings type is complete
      dashboardCardVisibility: settings.dashboardCardVisibility || DEFAULT_APP_SETTINGS.dashboardCardVisibility,
    };
    setFormData(initialSettings);
  }, [settings]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    // Simplified handleChange as no checkboxes are directly managed here anymore
    // If other checkboxes were to be added, more complex logic would be needed
    if (type === 'checkbox') {
        const { checked } = e.target as HTMLInputElement;
        setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }
  };


  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    onSaveSettings(formData);
  };

  const handleResetToDefaults = () => {
    if (window.confirm("Are you sure you want to reset all non-UI settings to their default values? This action cannot be undone from here.")) {
      // Reset only settings managed on this page, UI settings are separate
      const nonUISettingsToReset: Partial<AppSettings> = {
        companyName: DEFAULT_APP_SETTINGS.companyName,
        companyAddress: DEFAULT_APP_SETTINGS.companyAddress,
        companyPhone: DEFAULT_APP_SETTINGS.companyPhone,
        companyWebsite: DEFAULT_APP_SETTINGS.companyWebsite,
        defaultTicketPriority: DEFAULT_APP_SETTINGS.defaultTicketPriority,
        intakeAgreementText: DEFAULT_APP_SETTINGS.intakeAgreementText,
        completionAgreementText: DEFAULT_APP_SETTINGS.completionAgreementText,
      };
      setFormData(prev => ({ ...prev, ...nonUISettingsToReset }));
      // User must click "Save Settings" after reset for changes to take effect
    }
  };

  const renderSection = (title: string, children: React.ReactNode, icon?: React.ReactNode) => (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-slate-700 mb-5 border-b border-slate-200 pb-3 flex items-center">
        {icon}
        {title}
      </h2>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );

  const renderInput = (label: string, name: keyof AppSettings, type: string = "text", isTextarea: boolean = false, options?: any[]) => (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      {isTextarea ? (
        <textarea
          id={name}
          name={name}
          value={String(formData[name] === null || formData[name] === undefined ? '' : formData[name])}
          onChange={handleChange}
          rows={5}
          className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-[#003794] focus:border-[#003794] sm:text-sm"
        />
      ) : type === "select" && options ? (
        <select
          id={name}
          name={name}
          value={String(formData[name])}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-slate-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-[#003794] focus:border-[#003794] sm:text-sm"
        >
          {options.map(option => (
            <option key={option.value || option} value={option.value || option}>
              {option.label || option}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          id={name}
          name={name}
          value={String(formData[name] === null || formData[name] === undefined ? '' : formData[name])}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-[#003794] focus:border-[#003794] sm:text-sm"
        />
      )}
    </div>
  );

  return (
    <form onSubmit={handleSave} className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center mb-1">
            <CogWheelIcon />
            <h1 className="text-2xl font-bold text-[#003794]">Application Settings</h1>
        </div>
        <p className="text-sm text-slate-600">Manage general settings for the application. UI settings are now in "UI Customization".</p>
      </div>

      {renderSection("Company Information", <>
        {renderInput("Company Name", "companyName")}
        {renderInput("Address", "companyAddress")}
        {renderInput("Phone Number", "companyPhone")}
        {renderInput("Website URL", "companyWebsite")}
      </>)}

      {renderSection("Ticket Defaults", <>
        {renderInput("Default Ticket Priority", "defaultTicketPriority", "select", false, Object.values(TicketPriority))}
      </>)}

      {renderSection("User Agreements", <>
        {renderInput("Service Request / Intake Agreement", "intakeAgreementText", "text", true)}
        {renderInput("Service Completion Agreement", "completionAgreementText", "text", true)}
      </>)}

      {renderSection("Service Level Agreement (SLA) Rules (Conceptual)",
        <div className="space-y-3">
          <p className="text-xs text-slate-500">These are the current conceptual SLA rules based on ticket priority. They are not configurable in this version.</p>
          {SLA_RULES.map((rule: SLARule) => (
            <div key={rule.priority} className="p-3 border border-slate-200 rounded-md bg-slate-50">
              <h4 className="font-semibold text-sm text-slate-700">{rule.priority} Priority:</h4>
              <ul className="list-disc list-inside text-xs text-slate-600 pl-4">
                <li>Respond within: {rule.respondWithinHours} hour(s)</li>
                <li>Resolve within: {rule.resolveWithinHours} hour(s)</li>
              </ul>
            </div>
          ))}
        </div>,
        <ShieldCheckIcon />
      )}

      {renderSection("Integration Settings (Display Only)", <>
        <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Square Sandbox Application ID:</p>
            <p className="text-sm text-slate-600 bg-slate-100 p-2 rounded select-all">{SQUARE_SANDBOX_APP_ID}</p>
        </div>
         <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Square Sandbox Location ID:</p>
            <p className="text-sm text-slate-600 bg-slate-100 p-2 rounded select-all">{SQUARE_SANDBOX_LOCATION_ID}</p>
        </div>
        <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Gemini API Key:</p>
            <p className="text-sm text-slate-600">Configured via <code className="bg-slate-200 p-1 rounded text-xs">process.env.API_KEY</code>. Ensure this is set in your environment.</p>
        </div>
      </>)}


      <div className="bg-white p-6 rounded-lg shadow-md mt-6 flex justify-end space-x-3">
        <Button type="button" variant="ghost" onClick={handleResetToDefaults}>
          Reset Non-UI Defaults
        </Button>
        <Button type="submit" variant="primary">
          Save Settings
        </Button>
      </div>
    </form>
  );
};
